/**
 * BATCH 22 PART 6 — Workflows 64-75: Klaviyo + Attentive
 * 12 marketing workflow templates (1 Klaviyo, 11 Attentive)
 * Services: Attentive, Shopify, Twilio, HubSpot, OpenAI, Slack, Google Sheets
 */

// ============ HELPER FUNCTIONS ============

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

// ============ WORKFLOWS 64-75 ============

export const ELITE_BATCH22_PART6: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ── 64. Post-Purchase Education Series | Klaviyo | Pattern E ──
  "Post-Purchase Education Series": {
    nodes: [
      trigger("Klaviyo: Order fulfilled webhook", "webhook_app", {
        source: "Klaviyo",
        event: "order.fulfilled",
        howItStarts:
          "Fires when Klaviyo reports an order has been fulfilled and shipped to the customer.",
        setup:
          "Connect Klaviyo webhook for the Fulfilled Order event. Ensure order and product data are included in the payload.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract order and product details",
        280,
        "Extract order details from a Klaviyo fulfilled order webhook payload. Return JSON with customer_email, customer_name, product_name, product_category, order_total, and order_id.",
        "Klaviyo fulfilled order payload: {{{webhook_body}}}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "product_category", as: "product_category", type: "string" },
          { field: "order_total", as: "order_total", type: "number" },
          { field: "order_id", as: "order_id", type: "string" },
        ],
      ),
      n("3", "hitApi", "HubSpot: Look up customer purchase history", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{customer_email}"}]}]',
            },
          ],
          variables: [
            {
              name: "results[0].properties.total_purchases",
              customName: "total_purchases",
              type: "number",
            },
            {
              name: "results[0].properties.lifecyclestage",
              customName: "lifecycle_stage",
              type: "string",
            },
          ],
        },
      }),
      n("4", "condition", "Is this a first-time buyer?", 760, {
        conditionDetails: {
          conditions: [
            { field: "total_purchases", operator: "equals", value: "1" },
          ],
        },
      }),
      emailNode(
        "5",
        "Klaviyo: Send getting-started education email",
        1000,
        "{customer_email}",
        "Welcome to the family, {customer_name}! Here's your {product_name} getting-started guide",
        "Hi {customer_name},\\n\\nCongratulations on your first purchase! Your {product_name} is on its way.\\n\\nHere are the essentials to get the most out of your new {product_category} product:\\n\\n1. Unboxing & first-use tips\\n2. Care and maintenance guide\\n3. How to register for your warranty\\n\\nOrder #{order_id} — Total: ${order_total}\\n\\nWe're here if you need anything!",
      ),
      n("6", "delayAction", "Wait 7 days for product use", 1240, {
        delayDetails: { duration: 7, unit: "days" },
      }),
      n("7", "condition", "Has customer been delivered and engaged?", 1480, {
        conditionDetails: {
          conditions: [
            {
              field: "lifecycle_stage",
              operator: "not_equals",
              value: "churned",
            },
          ],
        },
      }),
      emailNode(
        "8",
        "Klaviyo: Send advanced tips and cross-sell email",
        1720,
        "{customer_email}",
        "{customer_name}, advanced tips for your {product_name} + items you'll love",
        "Hi {customer_name},\\n\\nYou've had your {product_name} for a week now — here are some advanced tips:\\n\\n• Pro technique for {product_category}\\n• Customer-favorite pairings\\n• Exclusive tutorial video access\\n\\nBased on your purchase, these complementary products are trending with other {product_category} buyers.\\n\\nThanks for being part of our community!",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Klaviyo post-purchase education: order fulfillment triggers AI extraction of product details, HubSpot checks purchase history, first-time buyers receive a getting-started guide, then after 7 days an advanced tips and cross-sell email is sent to engaged customers.",
  },

  // ── 65. SMS Welcome Journey | Attentive | Pattern A ──
  "SMS Welcome Journey": {
    nodes: [
      trigger("Attentive: SMS subscriber opt-in webhook", "webhook_app", {
        source: "Attentive",
        event: "subscriber.opted_in",
        howItStarts:
          "Fires when a new subscriber opts in to SMS marketing through Attentive.",
        setup:
          "Connect Attentive webhook for the subscriber opted-in event. Include phone, name, and opt-in source.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract subscriber details from opt-in",
        280,
        "Extract subscriber info from an Attentive SMS opt-in webhook. Return JSON with phone_number, subscriber_name, opt_in_source, and opt_in_keyword.",
        "Attentive opt-in payload: {{{webhook_body}}}",
        [
          { field: "phone_number", as: "phone_number", type: "string" },
          { field: "subscriber_name", as: "subscriber_name", type: "string" },
          { field: "opt_in_source", as: "opt_in_source", type: "string" },
          { field: "opt_in_keyword", as: "opt_in_keyword", type: "string" },
        ],
      ),
      n("3", "hitApi", "HubSpot: Check if existing customer", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"phone","operator":"EQ","value":"{phone_number}"}]}]',
            },
          ],
          variables: [
            { name: "total", customName: "contact_count", type: "number" },
            {
              name: "results[0].properties.total_purchases",
              customName: "past_purchases",
              type: "number",
            },
          ],
        },
      }),
      n("4", "condition", "Is this a brand-new subscriber?", 760, {
        conditionDetails: {
          conditions: [
            { field: "contact_count", operator: "equals", value: "0" },
          ],
        },
      }),
      smsNode(
        "5",
        "Twilio: Send SMS welcome with intro offer",
        1000,
        "{phone_number}",
        "Hey {subscriber_name}! Welcome to the fam 🎉 Thanks for joining via {opt_in_source}. Here's 15% off your first order — use code WELCOME15 at checkout. Shop now: https://store.example.com?utm=sms_welcome",
      ),
      n("6", "delayAction", "Wait 24 hours", 1240, {
        delayDetails: { duration: 24, unit: "hours" },
      }),
      n("7", "condition", "Has subscriber made a purchase yet?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "past_purchases", operator: "greater_than", value: "0" },
          ],
        },
      }),
      smsNode(
        "8",
        "Twilio: Send follow-up SMS with bestsellers",
        1720,
        "{phone_number}",
        "Hey {subscriber_name}, your 15% off code WELCOME15 is waiting! Check out our bestsellers before it expires: https://store.example.com/bestsellers?utm=sms_welcome_followup",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Attentive SMS welcome journey: new SMS opt-in triggers AI extraction of subscriber details, HubSpot checks existing customer status, new subscribers get a welcome SMS with intro offer, then 24 hours later non-purchasers receive a bestseller follow-up SMS.",
  },

  // ── 66. Email + SMS Welcome Orchestration | Attentive | Pattern A ──
  "Email + SMS Welcome Orchestration": {
    nodes: [
      trigger("Attentive: Dual-channel signup webhook", "webhook_app", {
        source: "Attentive",
        event: "subscriber.dual_opted_in",
        howItStarts:
          "Fires when a subscriber opts in to both email and SMS through Attentive.",
        setup:
          "Connect Attentive webhook for dual opt-in events. Ensure email, phone, and signup source are included.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract dual-channel subscriber info",
        280,
        "Extract subscriber info from an Attentive dual opt-in webhook. Return JSON with phone_number, email, subscriber_name, and signup_source.",
        "Attentive dual opt-in payload: {{{webhook_body}}}",
        [
          { field: "phone_number", as: "phone_number", type: "string" },
          { field: "email", as: "subscriber_email", type: "string" },
          { field: "subscriber_name", as: "subscriber_name", type: "string" },
          { field: "signup_source", as: "signup_source", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "HubSpot: Look up contact and channel preferences",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "filterGroups",
                value:
                  '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{subscriber_email}"}]}]',
              },
            ],
            variables: [
              { name: "total", customName: "existing_contact", type: "number" },
              {
                name: "results[0].properties.preferred_channel",
                customName: "preferred_channel",
                type: "string",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Does subscriber prefer SMS over email?", 760, {
        conditionDetails: {
          conditions: [
            { field: "preferred_channel", operator: "equals", value: "sms" },
          ],
        },
      }),
      smsNode(
        "5",
        "Twilio: Send SMS-first welcome with brand intro",
        1000,
        "{phone_number}",
        "Welcome {subscriber_name}! You're in. Expect exclusive drops & deals via text. Here's 10% off your first order: HELLO10. Shop: https://store.example.com?utm=sms_dual_welcome",
      ),
      n("6", "delayAction", "Wait 2 hours for channel stagger", 1240, {
        delayDetails: { duration: 2, unit: "hours" },
      }),
      emailNode(
        "7",
        "Send coordinated welcome email with full brand story",
        1480,
        "{subscriber_email}",
        "Welcome to our world, {subscriber_name}!",
        "Hi {subscriber_name},\\n\\nWelcome! We're glad you joined via {signup_source}.\\n\\nHere's what to expect from us:\\n• SMS: Flash sales, exclusive drops, time-sensitive deals\\n• Email: Brand stories, new collections, style guides\\n\\nYour 10% welcome code HELLO10 works across all channels.\\n\\nExplore our most-loved collections and find your new favorites.",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log dual-channel subscriber",
        1720,
        "DualChannelSignups",
        '[["{subscriber_name}","{subscriber_email}","{phone_number}","{signup_source}","{preferred_channel}","new"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Attentive dual-channel welcome orchestration: dual opt-in triggers AI extraction of subscriber info, HubSpot checks channel preferences, SMS-preferred subscribers get SMS first then a staggered email, email-preferred subscribers get email directly, with all dual-channel signups logged to Google Sheets.",
  },

  // ── 67. Double Opt-In Confirmation | Attentive | Pattern A ──
  "Double Opt-In Confirmation": {
    nodes: [
      trigger(
        "Attentive: SMS signup pending confirmation webhook",
        "webhook_app",
        {
          source: "Attentive",
          event: "subscriber.pending_confirmation",
          howItStarts:
            "Fires when a new subscriber initiates SMS opt-in and needs to confirm via double opt-in.",
          setup:
            "Connect Attentive webhook for pending confirmation events. Include phone, name, and confirmation token.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Extract signup and confirmation details",
        280,
        "Extract pending subscriber info from an Attentive double opt-in webhook. Return JSON with phone_number, subscriber_name, confirmation_token, and signup_page.",
        "Attentive pending confirmation payload: {{{webhook_body}}}",
        [
          { field: "phone_number", as: "phone_number", type: "string" },
          { field: "subscriber_name", as: "subscriber_name", type: "string" },
          {
            field: "confirmation_token",
            as: "confirmation_token",
            type: "string",
          },
          { field: "signup_page", as: "signup_page", type: "string" },
        ],
      ),
      n("3", "hitApi", "HubSpot: Check for prior opt-in history", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"phone","operator":"EQ","value":"{phone_number}"}]}]',
            },
          ],
          variables: [
            { name: "total", customName: "prior_records", type: "number" },
            {
              name: "results[0].properties.sms_confirmed",
              customName: "previously_confirmed",
              type: "string",
            },
          ],
        },
      }),
      n("4", "condition", "Has subscriber previously confirmed?", 760, {
        conditionDetails: {
          conditions: [
            {
              field: "previously_confirmed",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),
      smsNode(
        "5",
        "Twilio: Send double opt-in confirmation SMS",
        1000,
        "{phone_number}",
        "Hi {subscriber_name}! Reply YES to confirm your subscription and unlock exclusive deals. Msg&data rates may apply. Reply STOP to cancel.",
      ),
      n("6", "delayAction", "Wait 30 minutes for confirmation reply", 1240, {
        delayDetails: { duration: 30, unit: "minutes" },
      }),
      n("7", "condition", "Did subscriber reply YES within window?", 1480, {
        conditionDetails: {
          conditions: [
            {
              field: "confirmation_token",
              operator: "not_equals",
              value: "unconfirmed",
            },
          ],
        },
      }),
      smsNode(
        "8",
        "Twilio: Send confirmed welcome SMS with offer",
        1720,
        "{phone_number}",
        "You're confirmed, {subscriber_name}! Welcome aboard. As a thank you, here's 15% off your first order: CONFIRMED15. Start shopping: https://store.example.com?utm=sms_confirmed",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "8", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Attentive double opt-in confirmation: pending signup triggers AI extraction of subscriber data, HubSpot checks prior confirmation history, previously confirmed subscribers skip ahead, new subscribers receive a confirmation SMS, then confirmed subscribers get a welcome offer SMS.",
  },

  // ── 68. Cart Abandonment SMS | Attentive | Pattern B ──
  "Cart Abandonment SMS": {
    nodes: [
      trigger("Attentive: Cart abandoned event webhook", "webhook_app", {
        source: "Attentive",
        event: "cart.abandoned",
        howItStarts:
          "Fires when Attentive detects a subscriber has abandoned their shopping cart.",
        setup:
          "Connect Attentive webhook for cart abandoned events. Ensure cart items, total, and subscriber phone are in the payload.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract cart and subscriber details",
        280,
        "Extract cart abandonment details from an Attentive webhook. Return JSON with phone_number, subscriber_name, cart_total, top_product_name, top_product_image_url, cart_item_count, and cart_url.",
        "Attentive cart abandoned payload: {{{webhook_body}}}",
        [
          { field: "phone_number", as: "phone_number", type: "string" },
          { field: "subscriber_name", as: "subscriber_name", type: "string" },
          { field: "cart_total", as: "cart_total", type: "number" },
          { field: "top_product_name", as: "top_product_name", type: "string" },
          { field: "cart_item_count", as: "cart_item_count", type: "number" },
          { field: "cart_url", as: "cart_url", type: "string" },
        ],
      ),
      n("3", "hitApi", "Shopify: Look up customer and cart status", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/customers/search.json?query=phone:{phone_number}",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "customers[0].orders_count",
              customName: "past_orders",
              type: "number",
            },
            {
              name: "customers[0].total_spent",
              customName: "lifetime_spend",
              type: "string",
            },
          ],
        },
      }),
      n("4", "condition", "Is cart value above $75?", 760, {
        conditionDetails: {
          conditions: [
            { field: "cart_total", operator: "greater_than", value: "75" },
          ],
        },
      }),
      smsNode(
        "5",
        "Twilio: Send high-value cart recovery SMS",
        1000,
        "{phone_number}",
        "Hey {subscriber_name}, you left {top_product_name} + {cart_item_count} items (${cart_total}) in your cart! Complete your order now and get free shipping: {cart_url}?utm=sms_cart_recovery",
      ),
      n("6", "delayAction", "Wait 4 hours", 1240, {
        delayDetails: { duration: 4, unit: "hours" },
      }),
      n("7", "condition", "Has customer still not completed purchase?", 1480, {
        conditionDetails: {
          conditions: [
            {
              field: "past_orders",
              operator: "greater_than_or_equal",
              value: "0",
            },
          ],
        },
      }),
      smsNode(
        "8",
        "Twilio: Send urgency SMS with social proof",
        1720,
        "{phone_number}",
        "{subscriber_name}, your cart is selling out! {top_product_name} is in high demand. 1,200+ happy customers this week. Grab yours before it's gone: {cart_url}?utm=sms_cart_urgency",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Attentive cart abandonment SMS: cart abandoned event triggers AI extraction of cart details, Shopify looks up customer history, high-value carts get a recovery SMS with free shipping, then 4 hours later a social-proof urgency SMS follows for non-purchasers.",
  },

  // ── 69. Browse Abandonment SMS | Attentive | Pattern C ──
  "Browse Abandonment SMS": {
    nodes: [
      trigger(
        "Attentive: Product viewed without cart add webhook",
        "webhook_app",
        {
          source: "Attentive",
          event: "product.viewed_no_cart",
          howItStarts:
            "Fires when Attentive detects a subscriber viewed a product but did not add it to cart.",
          setup:
            "Connect Attentive webhook for browse abandonment events. Include product details, subscriber phone, and session data.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Extract browsed product details",
        280,
        "Extract browse abandonment details from an Attentive webhook. Return JSON with phone_number, subscriber_name, product_name, product_price, product_category, product_url, and view_count.",
        "Attentive browse abandonment payload: {{{webhook_body}}}",
        [
          { field: "phone_number", as: "phone_number", type: "string" },
          { field: "subscriber_name", as: "subscriber_name", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "product_price", as: "product_price", type: "number" },
          { field: "product_category", as: "product_category", type: "string" },
          { field: "product_url", as: "product_url", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "HubSpot: Look up subscriber affinity and history",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "filterGroups",
                value:
                  '[{"filters":[{"propertyName":"phone","operator":"EQ","value":"{phone_number}"}]}]',
              },
            ],
            variables: [
              {
                name: "results[0].properties.total_purchases",
                customName: "total_purchases",
                type: "number",
              },
              {
                name: "results[0].properties.favorite_category",
                customName: "favorite_category",
                type: "string",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Is this a returning customer?", 760, {
        conditionDetails: {
          conditions: [
            { field: "total_purchases", operator: "greater_than", value: "0" },
          ],
        },
      }),
      smsNode(
        "5",
        "Twilio: Send personalized browse reminder SMS",
        1000,
        "{phone_number}",
        "Hey {subscriber_name}, still thinking about {product_name} (${product_price})? We know you love {product_category} — this one's a best-seller! Take a closer look: {product_url}?utm=sms_browse",
      ),
      n("6", "delayAction", "Wait 24 hours", 1240, {
        delayDetails: { duration: 24, unit: "hours" },
      }),
      n("7", "condition", "Has subscriber still not added to cart?", 1480, {
        conditionDetails: {
          conditions: [
            {
              field: "total_purchases",
              operator: "greater_than_or_equal",
              value: "0",
            },
          ],
        },
      }),
      smsNode(
        "8",
        "Twilio: Send category recommendations SMS",
        1720,
        "{phone_number}",
        "{subscriber_name}, loved browsing {product_category}? Check out our top picks in that category — curated just for you: https://store.example.com/collections/{product_category}?utm=sms_browse_recs",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Attentive browse abandonment SMS: product viewed without cart add triggers AI extraction of product details, HubSpot checks subscriber purchase history and affinity, returning customers get a personalized browse reminder SMS, then 24 hours later non-converters receive category recommendation SMS.",
  },

  // ── 70. Checkout Abandonment Email + SMS | Attentive | Pattern B ──
  "Checkout Abandonment Email + SMS": {
    nodes: [
      trigger("Attentive: Checkout abandoned webhook", "webhook_app", {
        source: "Attentive",
        event: "checkout.abandoned",
        howItStarts:
          "Fires when Attentive detects a subscriber started checkout but did not complete the purchase.",
        setup:
          "Connect Attentive webhook for checkout abandoned events. Include checkout details, subscriber email, phone, and cart data.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract checkout and contact details",
        280,
        "Extract checkout abandonment details from an Attentive webhook. Return JSON with phone_number, email, subscriber_name, checkout_total, top_product_name, checkout_step, and checkout_url.",
        "Attentive checkout abandoned payload: {{{webhook_body}}}",
        [
          { field: "phone_number", as: "phone_number", type: "string" },
          { field: "email", as: "subscriber_email", type: "string" },
          { field: "subscriber_name", as: "subscriber_name", type: "string" },
          { field: "checkout_total", as: "checkout_total", type: "number" },
          { field: "top_product_name", as: "top_product_name", type: "string" },
          { field: "checkout_step", as: "checkout_step", type: "string" },
          { field: "checkout_url", as: "checkout_url", type: "string" },
        ],
      ),
      n("3", "hitApi", "Shopify: Verify checkout status and customer", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/customers/search.json?query=email:{subscriber_email}",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "customers[0].orders_count",
              customName: "previous_orders",
              type: "number",
            },
            {
              name: "customers[0].last_order_name",
              customName: "last_order_id",
              type: "string",
            },
          ],
        },
      }),
      n("4", "condition", "Is checkout total above $100?", 760, {
        conditionDetails: {
          conditions: [
            { field: "checkout_total", operator: "greater_than", value: "100" },
          ],
        },
      }),
      emailNode(
        "5",
        "Send email-first checkout recovery with order details",
        1000,
        "{subscriber_email}",
        "{subscriber_name}, you're so close! Complete your ${checkout_total} order",
        "Hi {subscriber_name},\\n\\nYou were almost there! Your checkout with {top_product_name} (${checkout_total}) is saved and waiting for you.\\n\\nYou stopped at the {checkout_step} step — pick up right where you left off:\\n\\n{checkout_url}?utm=email_checkout_recovery\\n\\nFree shipping on orders over $75. Need help? Just reply to this email.",
      ),
      n("6", "delayAction", "Wait 2 hours for email response", 1240, {
        delayDetails: { duration: 2, unit: "hours" },
      }),
      n("7", "condition", "Has checkout still not been completed?", 1480, {
        conditionDetails: {
          conditions: [
            {
              field: "previous_orders",
              operator: "greater_than_or_equal",
              value: "0",
            },
          ],
        },
      }),
      smsNode(
        "8",
        "Twilio: Escalate with SMS checkout reminder",
        1720,
        "{phone_number}",
        "{subscriber_name}, your {top_product_name} checkout (${checkout_total}) is still waiting! Complete it now before items sell out: {checkout_url}?utm=sms_checkout_rescue",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Attentive checkout abandonment email+SMS rescue: checkout abandoned triggers AI extraction of checkout details, Shopify verifies customer and checkout status, high-value checkouts get an email recovery first, then 2 hours later non-completers receive an escalation SMS reminder.",
  },

  // ── 71. Back-in-Stock SMS | Attentive | Pattern D ──
  "Back-in-Stock SMS": {
    nodes: [
      trigger("Attentive: Product restocked webhook", "webhook_app", {
        source: "Attentive",
        event: "product.back_in_stock",
        howItStarts:
          "Fires when Attentive detects a previously out-of-stock product is back in inventory.",
        setup:
          "Connect Attentive webhook for back-in-stock events. Include product details, subscriber list, and inventory count.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract restocked product details",
        280,
        "Extract back-in-stock product details from an Attentive webhook. Return JSON with product_name, product_price, product_url, stock_quantity, phone_number, and subscriber_name.",
        "Attentive back-in-stock payload: {{{webhook_body}}}",
        [
          { field: "product_name", as: "product_name", type: "string" },
          { field: "product_price", as: "product_price", type: "number" },
          { field: "product_url", as: "product_url", type: "string" },
          { field: "stock_quantity", as: "stock_quantity", type: "number" },
          { field: "phone_number", as: "phone_number", type: "string" },
          { field: "subscriber_name", as: "subscriber_name", type: "string" },
        ],
      ),
      n("3", "hitApi", "HubSpot: Verify subscriber interest and consent", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"phone","operator":"EQ","value":"{phone_number}"}]}]',
            },
          ],
          variables: [
            {
              name: "results[0].properties.sms_consent",
              customName: "sms_consent",
              type: "string",
            },
            {
              name: "results[0].properties.wishlist_items",
              customName: "wishlist_items",
              type: "string",
            },
          ],
        },
      }),
      n("4", "condition", "Is stock quantity limited (under 50 units)?", 760, {
        conditionDetails: {
          conditions: [
            { field: "stock_quantity", operator: "less_than", value: "50" },
          ],
        },
      }),
      smsNode(
        "5",
        "Twilio: Send urgent back-in-stock SMS alert",
        1000,
        "{phone_number}",
        "🚨 {subscriber_name}, {product_name} is BACK! Only {stock_quantity} left at ${product_price}. Grab yours before it sells out again: {product_url}?utm=sms_back_in_stock_urgent",
      ),
      n("6", "delayAction", "Wait 24 hours", 1240, {
        delayDetails: { duration: 24, unit: "hours" },
      }),
      n(
        "7",
        "condition",
        "Is the item still available and not purchased?",
        1480,
        {
          conditionDetails: {
            conditions: [
              { field: "stock_quantity", operator: "greater_than", value: "0" },
            ],
          },
        },
      ),
      smsNode(
        "8",
        "Twilio: Send stock scarcity follow-up SMS",
        1720,
        "{phone_number}",
        "Last chance, {subscriber_name}! {product_name} is almost gone again. Don't miss it this time — shop now: {product_url}?utm=sms_stock_last_chance",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Attentive back-in-stock SMS alert: restocked product triggers AI extraction of product details, HubSpot verifies subscriber interest and consent, limited-stock items get an urgent SMS alert, then 24 hours later a scarcity follow-up SMS is sent if the item is still available and unpurchased.",
  },

  // ── 72. Price Drop SMS | Attentive | Pattern D ──
  "Price Drop SMS": {
    nodes: [
      trigger("Attentive: Price drop detected webhook", "webhook_app", {
        source: "Attentive",
        event: "product.price_dropped",
        howItStarts:
          "Fires when Attentive detects a price drop on a product that subscribers have shown interest in.",
        setup:
          "Connect Attentive webhook for price drop events. Include product details, old/new price, and interested subscriber info.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract price drop and product details",
        280,
        "Extract price drop details from an Attentive webhook. Return JSON with product_name, original_price, new_price, discount_percent, product_url, phone_number, and subscriber_name.",
        "Attentive price drop payload: {{{webhook_body}}}",
        [
          { field: "product_name", as: "product_name", type: "string" },
          { field: "original_price", as: "original_price", type: "number" },
          { field: "new_price", as: "new_price", type: "number" },
          { field: "discount_percent", as: "discount_percent", type: "number" },
          { field: "product_url", as: "product_url", type: "string" },
          { field: "phone_number", as: "phone_number", type: "string" },
          { field: "subscriber_name", as: "subscriber_name", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "HubSpot: Check subscriber engagement and preferences",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "filterGroups",
                value:
                  '[{"filters":[{"propertyName":"phone","operator":"EQ","value":"{phone_number}"}]}]',
              },
            ],
            variables: [
              {
                name: "results[0].properties.sms_consent",
                customName: "sms_consent",
                type: "string",
              },
              {
                name: "results[0].properties.price_sensitivity",
                customName: "price_sensitivity",
                type: "string",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Is the discount 20% or more?", 760, {
        conditionDetails: {
          conditions: [
            {
              field: "discount_percent",
              operator: "greater_than_or_equal",
              value: "20",
            },
          ],
        },
      }),
      smsNode(
        "5",
        "Twilio: Send price drop SMS alert",
        1000,
        "{phone_number}",
        "💰 {subscriber_name}, price drop alert! {product_name} just went from ${original_price} to ${new_price} — that's {discount_percent}% off! Shop now: {product_url}?utm=sms_price_drop",
      ),
      n("6", "delayAction", "Wait 48 hours", 1240, {
        delayDetails: { duration: 48, unit: "hours" },
      }),
      n("7", "condition", "Is the product still at the reduced price?", 1480, {
        conditionDetails: {
          conditions: [
            {
              field: "new_price",
              operator: "less_than",
              value: "{original_price}",
            },
          ],
        },
      }),
      smsNode(
        "8",
        "Twilio: Send urgency follow-up price SMS",
        1720,
        "{phone_number}",
        "{subscriber_name}, {product_name} is still {discount_percent}% off at ${new_price} (was ${original_price}) — but the sale won't last much longer! Grab it: {product_url}?utm=sms_price_drop_urgency",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Attentive price drop SMS alert: price decrease triggers AI extraction of pricing details, HubSpot checks subscriber engagement and price sensitivity, significant discounts (20%+) get an immediate SMS alert, then 48 hours later a time-pressure follow-up SMS is sent if the deal is still active.",
  },

  // ── 73. Low Inventory SMS | Attentive | Pattern D ──
  "Low Inventory SMS": {
    nodes: [
      trigger("Attentive: Low inventory threshold webhook", "webhook_app", {
        source: "Attentive",
        event: "product.low_inventory",
        howItStarts:
          "Fires when Attentive detects a product's inventory has dropped below the configured threshold.",
        setup:
          "Connect Attentive webhook for low inventory events. Include product details, remaining stock, and interested subscribers.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract low-stock product details",
        280,
        "Extract low inventory details from an Attentive webhook. Return JSON with product_name, product_price, remaining_stock, product_url, phone_number, and subscriber_name.",
        "Attentive low inventory payload: {{{webhook_body}}}",
        [
          { field: "product_name", as: "product_name", type: "string" },
          { field: "product_price", as: "product_price", type: "number" },
          { field: "remaining_stock", as: "remaining_stock", type: "number" },
          { field: "product_url", as: "product_url", type: "string" },
          { field: "phone_number", as: "phone_number", type: "string" },
          { field: "subscriber_name", as: "subscriber_name", type: "string" },
        ],
      ),
      n("3", "hitApi", "HubSpot: Verify subscriber interest and consent", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"phone","operator":"EQ","value":"{phone_number}"}]}]',
            },
          ],
          variables: [
            {
              name: "results[0].properties.sms_consent",
              customName: "sms_consent",
              type: "string",
            },
            {
              name: "results[0].properties.browsed_products",
              customName: "browsed_products",
              type: "string",
            },
          ],
        },
      }),
      n("4", "condition", "Is stock critically low (under 10 units)?", 760, {
        conditionDetails: {
          conditions: [
            { field: "remaining_stock", operator: "less_than", value: "10" },
          ],
        },
      }),
      smsNode(
        "5",
        "Twilio: Send critical low-stock urgency SMS",
        1000,
        "{phone_number}",
        "⚠️ {subscriber_name}, only {remaining_stock} left of {product_name} at ${product_price}! This sells out fast — don't miss it: {product_url}?utm=sms_low_stock_critical",
      ),
      n("6", "delayAction", "Wait 12 hours", 1240, {
        delayDetails: { duration: 12, unit: "hours" },
      }),
      n("7", "condition", "Is the product still in stock?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "remaining_stock", operator: "greater_than", value: "0" },
          ],
        },
      }),
      smsNode(
        "8",
        "Twilio: Send last-chance scarcity SMS",
        1720,
        "{phone_number}",
        "LAST CALL {subscriber_name}! {product_name} is nearly sold out — only a handful left at ${product_price}. This is it: {product_url}?utm=sms_low_stock_last_chance",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Attentive low inventory SMS urgency: low stock threshold triggers AI extraction of product details, HubSpot verifies subscriber interest and consent, critically low items (under 10) get an immediate urgency SMS, then 12 hours later a last-chance scarcity SMS follows if stock remains.",
  },

  // ── 74. Flash Sale Countdown SMS | Attentive | Pattern D ──
  "Flash Sale Countdown SMS": {
    nodes: [
      trigger("Attentive: Flash sale launched webhook", "webhook_app", {
        source: "Attentive",
        event: "promotion.flash_sale_started",
        howItStarts:
          "Fires when a flash sale is created and activated in Attentive.",
        setup:
          "Connect Attentive webhook for flash sale launch events. Include sale name, discount details, end time, and featured products.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract flash sale details",
        280,
        "Extract flash sale details from an Attentive webhook. Return JSON with sale_name, discount_percent, sale_end_time, hours_remaining, featured_product, sale_url, phone_number, and subscriber_name.",
        "Attentive flash sale payload: {{{webhook_body}}}",
        [
          { field: "sale_name", as: "sale_name", type: "string" },
          { field: "discount_percent", as: "discount_percent", type: "number" },
          { field: "hours_remaining", as: "hours_remaining", type: "number" },
          { field: "featured_product", as: "featured_product", type: "string" },
          { field: "sale_url", as: "sale_url", type: "string" },
          { field: "phone_number", as: "phone_number", type: "string" },
          { field: "subscriber_name", as: "subscriber_name", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "HubSpot: Check subscriber segment and engagement",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "filterGroups",
                value:
                  '[{"filters":[{"propertyName":"phone","operator":"EQ","value":"{phone_number}"}]}]',
              },
            ],
            variables: [
              {
                name: "results[0].properties.engagement_score",
                customName: "engagement_score",
                type: "number",
              },
              {
                name: "results[0].properties.sms_consent",
                customName: "sms_consent",
                type: "string",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Is the sale highly discounted (30%+ off)?", 760, {
        conditionDetails: {
          conditions: [
            {
              field: "discount_percent",
              operator: "greater_than_or_equal",
              value: "30",
            },
          ],
        },
      }),
      smsNode(
        "5",
        "Twilio: Send flash sale launch SMS with countdown",
        1000,
        "{phone_number}",
        "🔥 FLASH SALE {subscriber_name}! {sale_name} is LIVE — {discount_percent}% off everything for {hours_remaining} hours only! Shop {featured_product} & more: {sale_url}?utm=sms_flash_sale",
      ),
      n("6", "delayAction", "Wait until 2 hours before sale ends", 1240, {
        delayDetails: { duration: 4, unit: "hours" },
      }),
      n("7", "condition", "Is the sale still active?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "hours_remaining", operator: "greater_than", value: "0" },
          ],
        },
      }),
      smsNode(
        "8",
        "Twilio: Send last-hours countdown SMS",
        1720,
        "{phone_number}",
        "⏰ {subscriber_name}, {sale_name} ends in 2 HOURS! {discount_percent}% off is almost over. Last chance to save on {featured_product}: {sale_url}?utm=sms_flash_final",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Attentive flash sale countdown SMS: flash sale launch triggers AI extraction of sale details, HubSpot checks subscriber engagement, high-discount sales (30%+) get an immediate launch SMS, then a countdown last-hours SMS follows before the sale ends for active sales.",
  },

  // ── 75. VIP Early Access SMS | Attentive | Pattern H ──
  "VIP Early Access SMS": {
    nodes: [
      trigger("Attentive: VIP early access event webhook", "webhook_app", {
        source: "Attentive",
        event: "loyalty.vip_early_access",
        howItStarts:
          "Fires when Attentive triggers a VIP early access event for an upcoming sale or product drop.",
        setup:
          "Connect Attentive webhook for VIP early access events. Include VIP tier, access details, and subscriber info.",
        inputFields: [],
      }),
      n("2", "hitApi", "HubSpot: Look up VIP loyalty data and LTV", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"phone","operator":"EQ","value":"{phone_number}"}]}]',
            },
          ],
          variables: [
            {
              name: "results[0].properties.vip_tier",
              customName: "vip_tier",
              type: "string",
            },
            {
              name: "results[0].properties.lifetime_value",
              customName: "lifetime_value",
              type: "number",
            },
            {
              name: "results[0].properties.firstname",
              customName: "subscriber_name",
              type: "string",
            },
            {
              name: "results[0].properties.phone",
              customName: "phone_number",
              type: "string",
            },
            {
              name: "results[0].properties.loyalty_points",
              customName: "loyalty_points",
              type: "number",
            },
          ],
        },
      }),
      n("3", "condition", "Is subscriber a Gold or Platinum VIP?", 520, {
        conditionDetails: {
          conditions: [
            { field: "vip_tier", operator: "equals", value: "gold" },
          ],
        },
      }),
      smsNode(
        "4",
        "Twilio: Send VIP early access SMS with exclusive link",
        760,
        "{phone_number}",
        "👑 {subscriber_name}, as a {vip_tier} VIP you get FIRST ACCESS! Our biggest drop of the season is live for VIPs only — 48 hours before everyone else. Shop your exclusive link: https://store.example.com/vip-access?utm=sms_vip_early&tier={vip_tier}",
      ),
      n("5", "delayAction", "Wait 5 days for VIP engagement", 1000, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      n("6", "condition", "Has VIP redeemed their early access?", 1240, {
        conditionDetails: {
          conditions: [
            { field: "loyalty_points", operator: "greater_than", value: "500" },
          ],
        },
      }),
      smsNode(
        "7",
        "Twilio: Send VIP redemption reminder SMS",
        1480,
        "{phone_number}",
        "{subscriber_name}, your {vip_tier} VIP early access window is closing! You have {loyalty_points} points to use — combine them with this drop for max savings: https://store.example.com/vip-access?utm=sms_vip_reminder",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log VIP early access engagement",
        1720,
        "VIPEarlyAccess",
        '[["{subscriber_name}","{phone_number}","{vip_tier}","{lifetime_value}","{loyalty_points}","early_access_sent"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "7", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_2"),
      e("6", "8", "output_1"),
    ],
    summary:
      "Attentive VIP early access SMS: VIP event triggers HubSpot lookup of loyalty tier and LTV, Gold/Platinum VIPs receive an exclusive early access SMS, after 5 days non-redeemers get a points-based reminder SMS, and all VIP engagement is logged to Google Sheets for tracking.",
  },
};
